'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  GearIcon,
  SidebarSimpleIcon,
  SignOutIcon,
} from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'
import { createClient } from '@/account/client'
import { getEnvironmentStorageKey } from '@/lib/environment'
import { SidebarNav } from './sidebar-nav'
import { SIDEBAR_TEST_ID } from './test-ids'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  onCaptureOpen?: () => void
  onSearchOpen?: () => void
  className?: string
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export function Sidebar({
  collapsed,
  onToggle,
  onCaptureOpen,
  onSearchOpen,
  className,
}: SidebarProps) {
  const router = useRouter()

  const handleSignOut = async () => {
    localStorage.removeItem(getEnvironmentStorageKey('automerge-doc-url'))
    const supabase = createClient()
    await supabase.auth.signOut()
    router.push('/auth/login')
  }

  const iconBtnClass = cn(
    'flex items-center justify-center rounded-md p-1.5 transition-colors',
    'text-muted-foreground hover:text-foreground hover:bg-accent/10',
    'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
  )

  return (
    <aside
      className={cn(
        'border-border/50 bg-background flex h-full flex-col border-r',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-12' : 'w-56',
        className,
      )}
      aria-label="Sidebar"
      data-testid={SIDEBAR_TEST_ID}
    >
      {/* Header — logo + actions in one row (expanded) or stacked (collapsed) */}
      {collapsed ? (
        <div className="flex flex-col items-center gap-1 pt-4 pb-2">
          <Link
            href="/app"
            className="focus-visible:ring-ring mb-1 rounded-md transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:outline-none"
            aria-label="Home"
          >
            <span className="font-logo text-brand-accent text-xl">K</span>
          </Link>
          <button
            type="button"
            onClick={onToggle}
            className={iconBtnClass}
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <SidebarSimpleIcon
              className="h-[18px] w-[18px] shrink-0 rotate-180"
              weight="regular"
            />
          </button>
          <button
            type="button"
            onClick={onSearchOpen}
            className={iconBtnClass}
            aria-label="Search"
            title="Search"
          >
            <MagnifyingGlassIcon
              className="h-[18px] w-[18px] shrink-0"
              weight="regular"
            />
          </button>
          <button
            type="button"
            onClick={onCaptureOpen}
            className={iconBtnClass}
            aria-label="New capture"
            title="New capture"
          >
            <PlusIcon className="h-[18px] w-[18px] shrink-0" weight="bold" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 px-2 pt-4 pb-2">
          <button
            type="button"
            onClick={onToggle}
            className={iconBtnClass}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <SidebarSimpleIcon
              className="h-[18px] w-[18px] shrink-0"
              weight="regular"
            />
          </button>

          <Link
            href="/app"
            className="focus-visible:ring-ring flex-1 rounded-md px-1 transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:outline-none"
            aria-label="Home"
          >
            <span className="font-logo text-xl tracking-tight">
              konstant<span className="text-brand-accent">out</span>
            </span>
          </Link>

          <button
            type="button"
            onClick={onSearchOpen}
            className={iconBtnClass}
            aria-label="Search"
            title="Search"
          >
            <MagnifyingGlassIcon
              className="h-[18px] w-[18px] shrink-0"
              weight="regular"
            />
          </button>
          <button
            type="button"
            onClick={onCaptureOpen}
            className={iconBtnClass}
            aria-label="New capture"
            title="New capture"
          >
            <PlusIcon className="h-[18px] w-[18px] shrink-0" weight="bold" />
          </button>
        </div>
      )}

      {/* Nav — scrollable middle area */}
      <div
        className={cn(
          'flex-1 overflow-y-auto px-3 py-2',
          collapsed && 'px-1.5',
        )}
      >
        <SidebarNav collapsed={collapsed} />
      </div>

      {/* Footer */}
      <div
        className={cn(
          'border-border/50 flex flex-col gap-0.5 border-t px-3 py-2',
          collapsed && 'items-center px-1.5',
        )}
      >
        <Link
          href="/app/settings"
          className={cn(
            'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
            'text-muted-foreground hover:text-foreground hover:bg-accent/5',
            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
            collapsed && 'justify-center px-0',
          )}
          aria-label="Settings"
          title={collapsed ? 'Settings' : undefined}
        >
          <GearIcon className="h-[18px] w-[18px] shrink-0" weight="regular" />
          {!collapsed && <span>Settings</span>}
        </Link>

        <button
          type="button"
          onClick={handleSignOut}
          className={cn(
            'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
            'text-muted-foreground hover:text-destructive hover:bg-destructive/5',
            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
            collapsed && 'justify-center px-0',
          )}
          aria-label="Sign out"
          title={collapsed ? 'Sign out' : undefined}
        >
          <SignOutIcon
            className="h-[18px] w-[18px] shrink-0"
            weight="regular"
          />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  )
}
